import React from 'react';
import { Box, Modal, Typography, IconButton } from '@mui/material';
import { Close } from '@mui/icons-material';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import CustomButton from '../Button/CustomButton';
import { IAdvertisement } from '../../types/global/media.types';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { MixPanel, MIXPANEL_EVENT } from '../../analytics/Mixpanel';

interface InterstitialAdBannerProps {
    ad: IAdvertisement;
    onPrimary?: () => void;
    onSecondary?: () => void;
    onClose?: () => void;
}

const InterstitialAdBanner: React.FC<InterstitialAdBannerProps> = ({ ad, onPrimary, onSecondary, onClose }) => {
    const { themeStyle } = useThemeContext();

    const handlePrimaryClick = () => {
        if (ad.click_through) {
            window.open(ad.click_through, '_blank', 'noopener,noreferrer');
        }
        MixPanel.track(MIXPANEL_EVENT.INTERSTITIAL_AD_CLICK, {
            ad: {
                ...ad,
            },
            clicked: 'click_through',
        });
        onPrimary?.();
        onClose?.();
    };

    const handleSecondaryClick = () => {
        if (ad.secondary_call_to_action) {
            window.open(ad.secondary_click_through, '_blank', 'noopener,noreferrer');
        }
        MixPanel.track(MIXPANEL_EVENT.INTERSTITIAL_AD_CLICK, {
            ad: {
                ...ad,
            },
            clicked: 'secondary_click_through',
        });
        onSecondary?.();
        onClose?.();
    };

    return (
        <Modal
            open={true}
            onClose={onClose}
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                outline: 'none',
                '&:focus': {
                    outline: 'none',
                },
            }}>
            <Box
                sx={{
                    position: 'relative',
                    width: '100%',
                    maxWidth: 600,
                    borderRadius: 3,
                    overflow: 'hidden',
                    boxShadow: 24,
                    border: '1px solid #e0e0e0',
                    outline: 'none',
                    '&:focus': {
                        outline: 'none',
                    },
                }}>
                {/* Close Icon Button */}
                <IconButton
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        zIndex: 1000,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        color: 'white',
                        outline: 'none',
                        '&:focus': {
                            outline: 'none',
                        },
                        '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        },
                    }}>
                    <Close />
                </IconButton>

                {/* Background Image Section */}
                <Box
                    sx={{
                        position: 'relative',
                        height: 500,
                        backgroundImage: ad.feature_image ? `url(${ad.feature_image})` : undefined,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundColor: theme[themeStyle].deep_grey,
                    }}
                />

                {/* Title and Description Section */}
                <Box
                    sx={{
                        p: 3,
                        backgroundColor: theme[themeStyle].deep_grey_lighter || '#fff',
                    }}>
                    {ad.ad_title && (
                        <Typography
                            variant="h4"
                            component="h2"
                            sx={{
                                fontSize: { xs: '1rem', sm: '1.5rem' },
                                fontWeight: 'bold',
                                color: theme[themeStyle].tag_white,
                                mb: ad.description ? 2 : 0,
                            }}>
                            {ad.ad_title}
                        </Typography>
                    )}
                    {ad.description && (
                        <Typography
                            variant="body1"
                            sx={{
                                fontSize: { xs: '0.6rem', sm: '0.8rem' },
                                color: theme[themeStyle].tag_white,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                mb: 1,
                            }}>
                            {ad.description}
                        </Typography>
                    )}
                </Box>

                {/* Buttons Section */}
                <Box
                    sx={{
                        display: 'flex',
                        gap: 2,
                        p: 3,
                        backgroundColor: theme[themeStyle].deep_grey_lighter || '#fff',
                        flexDirection: { xs: 'column', sm: 'row' },
                    }}>
                    <CustomButton
                        label={ad.secondary_call_to_action || 'Learn More'}
                        onClick={handleSecondaryClick}
                        buttonProps={{
                            variant: 'outlined',
                            sx: {
                                flex: 1,
                                py: 1.5,
                                fontSize: '1rem',
                                fontWeight: 600,
                                outline: 'none',
                                '&:focus': {
                                    outline: 'none',
                                },
                            },
                        }}
                    />
                    <CustomButton
                        label={ad.call_to_action || 'Claim Offer'}
                        onClick={handlePrimaryClick}
                        buttonProps={{
                            variant: 'contained',
                            sx: {
                                flex: 1,
                                py: 1.5,
                                fontSize: '1rem',
                                fontWeight: 600,
                                outline: 'none',
                                '&:focus': {
                                    outline: 'none',
                                },
                            },
                        }}
                    />
                </Box>
            </Box>
        </Modal>
    );
};

export default InterstitialAdBanner;
